import { Key } from "react";

import type { AutosaveFields } from "shared/autosave";
import { AutosaveEngine, createAutosaveEngine } from "shared/autosave";
import type { Vero1ItemEdit, Vero1Severity } from "shared/interfaces/api/vero1";

import { persistItemEdit, Vero1ConflictCode } from "./persistItem";
import {
  emitAutosaveOutcome,
  Vero1AutosaveTerminal,
  Vero1OutcomeCode,
} from "./vero1Telemetry";

export type Vero1AutosaveFields = Vero1ItemEdit & AutosaveFields;

type SlimEditMutation = Parameters<typeof persistItemEdit>[0];

export interface Vero1AutosaveQueue {
  engine: AutosaveEngine<Vero1AutosaveFields>;
  /** Re-pointed each render — the queue outlives any single mount. */
  commitRef: { current: SlimEditMutation | null };
  lockedRef: { current: boolean };
  onBlockedRef: { current: ((code: Vero1ConflictCode) => void) | null };
  onRejectedRef: {
    current:
      | ((itemId: string, availableSeverities: Vero1Severity[]) => void)
      | null;
  };
  onNameRejectedRef: {
    current: ((itemId: string, nameCandidates: string[]) => void) | null;
  };
  dealIdRef: { current: Key | null };
}

const REPORTED_OUTCOMES: ReadonlySet<string> = new Set([
  "blocked",
  "conflict",
  "failed",
]);

// module-scoped per CHECKLIST: an unmount must not kill saves still in
// flight, and a new draft gets a fresh queue by construction — a queue
// stopped by a blocked write can never haunt the next review on the deal
const queues = new Map<string, Vero1AutosaveQueue>();

export const getVero1AutosaveQueue = (checklistId: Key): Vero1AutosaveQueue => {
  const key = String(checklistId);
  const existing = queues.get(key);
  if (existing) return existing;

  const commitRef: Vero1AutosaveQueue["commitRef"] = { current: null };
  const lockedRef: Vero1AutosaveQueue["lockedRef"] = { current: false };
  const onBlockedRef: Vero1AutosaveQueue["onBlockedRef"] = { current: null };
  const onRejectedRef: Vero1AutosaveQueue["onRejectedRef"] = { current: null };
  const onNameRejectedRef: Vero1AutosaveQueue["onNameRejectedRef"] = {
    current: null,
  };
  const dealIdRef: Vero1AutosaveQueue["dealIdRef"] = { current: null };

  const lastCodeByEntity = new Map<string, Vero1OutcomeCode>();

  const engine = createAutosaveEngine<Vero1AutosaveFields>({
    commit: async (entityId, fields, token) => {
      const editItem = commitRef.current;
      if (!editItem || token === null) return { status: "failed" };
      // SEAM — per-item tokens: when kw/product-2534-autosave-transport
      // deploys, pass expectedItemUpdatedAt (item.updatedAt) here
      const outcome = await persistItemEdit(editItem, {
        itemId: entityId,
        fields,
        expectedToken: token,
        locked: lockedRef.current,
      });
      if (outcome.status === "saved") {
        lastCodeByEntity.delete(entityId);
        return { status: "saved", newToken: outcome.newToken };
      }
      if (outcome.status === "blocked") {
        lastCodeByEntity.set(entityId, outcome.code);
        onBlockedRef.current?.(outcome.code);
        return { status: "blocked" };
      }
      if (outcome.status === "rejected") {
        lastCodeByEntity.delete(entityId);
        emitAutosaveOutcome({
          outcome: "rejected",
          conflictCode: outcome.code,
          queue: "item",
          dealId: dealIdRef.current ?? "",
          checklistId: key,
        });
        if (outcome.code === "name-unavailable") {
          onNameRejectedRef.current?.(entityId, outcome.nameCandidates);
          return { status: "rejected", dropFields: ["overrideOutcomeName"] };
        }
        onRejectedRef.current?.(entityId, outcome.availableSeverities);
        return { status: "rejected", dropFields: ["overrideSeverity"] };
      }
      if (outcome.status === "conflict") {
        lastCodeByEntity.set(entityId, outcome.code);
        return { status: "conflict" };
      }
      lastCodeByEntity.delete(entityId);
      return { status: outcome.status };
    },
    onEvent: (event) => {
      if (event.type !== "entity" || !REPORTED_OUTCOMES.has(event.status)) {
        return;
      }
      emitAutosaveOutcome({
        outcome: event.status as Vero1AutosaveTerminal,
        conflictCode: lastCodeByEntity.get(event.entityId) ?? null,
        queue: "item",
        dealId: dealIdRef.current ?? "",
        checklistId: key,
      });
    },
  });

  const handle = {
    engine,
    commitRef,
    lockedRef,
    onBlockedRef,
    onRejectedRef,
    onNameRejectedRef,
    dealIdRef,
  };
  queues.set(key, handle);
  return handle;
};

/** The checklist is gone (discarded) or superseded — pending edits have
 *  nothing to land on. */
export const disposeVero1AutosaveQueue = (checklistId: Key) => {
  const handle = queues.get(String(checklistId));
  if (!handle) return;
  handle.engine.dropAll();
  handle.engine.stop();
  queues.delete(String(checklistId));
};
